import {
  Injectable,
  Injector,
  ResourceRef,
  Signal,
  computed,
  inject,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';

import { ContentService } from '../../../../generated/api/content/content.service';
import { notFoundIsDeadEnd } from '@/core/http/not-found-redirect';
import { GetTeacherHome200 } from '../../../../generated/model/getTeacherHome200';
import { GradeSlug } from '@/shared/utils/grade/grade';
import {
  BannerView,
  LessonPlanView,
  LessonView,
  NoticeView,
  TrackView,
} from '@/shared/content/teacher-content.types';
import { TeacherContentSource } from '@/shared/content/teacher-content.contract';
import {
  bannerFromTeacherHome,
  lessonPlanFromTeacherHome,
  lessonsFromTeacherHome,
  tracksFromTeacherHome,
} from '@/shared/content/teacher-content-mappers/teacher-content.mappers';
import { TEACHER_NOTICES } from '@/shared/content/teacher-notices';
import { resourceScreenReplaced } from '@/core/http/problem-details/problem-details';

@Injectable()
export class StrapiTeacherContent implements TeacherContentSource {
  private readonly content = inject(ContentService);
  private readonly injector = inject(Injector);
  private readonly cache = new Map<GradeSlug, ResourceRef<GetTeacherHome200 | undefined>>();

  private homeFor(year: GradeSlug): ResourceRef<GetTeacherHome200 | undefined> {
    const cached = this.cache.get(year);
    if (cached) return cached;
    // homeFor() is often triggered lazily from inside a page's computed() (first read of
    // lessonsFor/bannerFor/isLoading); rxResource() creates an effect() internally, which
    // Angular forbids from a reactive context, so resource creation must escape it.
    const home = untracked(() =>
      runInInjectionContext(this.injector, () =>
        rxResource({
          params: () => year,
          // The year's curriculum *is* what /teacher/curriculum/:year names, so a 404 is a dead end.
          stream: ({ params }) =>
            this.content.getTeacherHome<GetTeacherHome200>(params, {
              context: notFoundIsDeadEnd(),
            }),
        }),
      ),
    );
    this.cache.set(year, home);
    return home;
  }

  lessonsFor(year: GradeSlug): Signal<readonly LessonView[]> {
    const home = this.homeFor(year);
    return computed(() => (home.hasValue() ? lessonsFromTeacherHome(home.value()) : []));
  }

  tracksFor(year: GradeSlug): Signal<readonly TrackView[]> {
    const home = this.homeFor(year);
    return computed(() => (home.hasValue() ? tracksFromTeacherHome(home.value()) : []));
  }

  bannerFor(year: GradeSlug): Signal<BannerView | null> {
    const home = this.homeFor(year);
    return computed(() => (home.hasValue() ? bannerFromTeacherHome(home.value()) : null));
  }

  lessonPlanFor(year: GradeSlug, lessonNumber: number): Signal<LessonPlanView | null> {
    const home = this.homeFor(year);
    return computed(() =>
      home.hasValue() ? lessonPlanFromTeacherHome(home.value(), lessonNumber) : null,
    );
  }

  isLoading(year: GradeSlug): Signal<boolean> {
    const home = this.homeFor(year);

    return computed(() => home.isLoading() || resourceScreenReplaced(home.error()));
  }

  loadFailed(year: GradeSlug): Signal<boolean> {
    const home = this.homeFor(year);

    return computed(() => home.error() !== undefined && !resourceScreenReplaced(home.error()));
  }

  notices(): Signal<readonly NoticeView[]> {
    return signal(TEACHER_NOTICES).asReadonly();
  }
}
